import { z } from 'zod';

import { cursorSearchSchema } from '../data-table/cursor-search';

import {
  filterParamKeys,
  parseFilterValues,
  serializeFilterValues,
  type FilterField,
} from './filter-codec';
import { nonEmpty } from './list-state';

/**
 * A flat filter param as the route holds it. The router parses `total_gte=10`
 * into a number and `lowStock=true` into a boolean, and writes them back the
 * same way; keeping them primitive keeps the URL free of `%22` quotes.
 */
export type SearchParamValue = string | number | boolean;

/** Param names of one filter, as a type: `status` | `status_not`, `total_gte` | `total_lte`. */
type ParamKeysOf<F> = F extends {
  type: 'number-range' | 'date-range';
  key: infer K extends string;
}
  ? `${K}_gte` | `${K}_lte`
  : F extends { type: 'enum' | 'async'; key: infer K extends string; negatable: true }
    ? K | `${K}_not`
    : F extends { key: infer K extends string }
      ? K
      : never;

type SortParam<S extends string> = S | `-${S}`;

type CursorShape = typeof cursorSearchSchema.shape;

export type IndexSearchShape<F extends FilterField, S extends string> = {
  [K in keyof CursorShape]: z.ZodType<z.output<CursorShape[K]> | undefined>;
} & ([S] extends [never] ? unknown : { sort: z.ZodType<SortParam<S> | undefined> }) &
  Record<ParamKeysOf<F>, z.ZodType<SearchParamValue | undefined>>;

export interface IndexSearchOptions<S extends string> {
  /** Fields the endpoint sorts on. `sort` accepts each ascending and `-`descending. */
  sort?: readonly S[];
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const DECIMAL = /^-?\d+(?:\.\d+)?$/;
const INTEGER = /^-?\d+$/;

const text = z.union([z.string().trim().min(1), z.number()]);
const bool = z.union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')]);
const day = z.string().regex(ISO_DAY);

function numeric(pattern: RegExp) {
  const number = pattern === INTEGER ? z.number().int() : z.number();
  return z.union([number, z.string().regex(pattern).transform(Number)]);
}

function paramSchema(def: FilterField): z.ZodType<SearchParamValue> {
  switch (def.type) {
    case 'enum':
    case 'async':
    case 'text':
      return text;
    case 'boolean':
      return bool;
    case 'number-range':
      // Money is minor units, so whole numbers only.
      return numeric(def.currency ? INTEGER : DECIMAL);
    case 'date-range':
      return day;
  }
}

/**
 * The route's `validateSearch` for an index table on a cursor-paginated
 * endpoint: `cursor`, `limit`, `q`, `sort` (one of `options.sort`, ascending
 * or `-`descending) and every flat filter param (`status`, `status_not`,
 * `total_gte`, `total_lte`...). A malformed value drops that one param instead
 * of failing the route.
 *
 * Pass the same filters the page gives `useDataTable`, or just their `key`,
 * `type`, `negatable` and `currency`; declare them `as const` and the search
 * type names every param. `.extend()` it for params the table does not own.
 */
export function indexSearchSchema<const F extends FilterField, const S extends string = never>(
  filters: readonly F[],
  options: IndexSearchOptions<S> = {},
) {
  const base = cursorSearchSchema.shape;
  const shape: Record<string, z.ZodType> = {
    cursor: z
      .union([base.cursor.unwrap().min(1), z.number().transform(String)])
      .optional()
      .catch(undefined),
    limit: base.limit.catch(undefined),
    q: z
      .union([base.q.unwrap(), z.number().transform(String)])
      .optional()
      .catch(undefined),
  };
  const sort = options.sort ?? [];
  if (sort.length > 0) {
    const values = sort.flatMap((field) => [field, `-${field}`]);
    shape.sort = z
      .enum(values as [string, ...string[]])
      .optional()
      .catch(undefined);
  }
  for (const def of filters) {
    for (const key of filterParamKeys(def)) {
      shape[key] = paramSchema(def).optional().catch(undefined);
    }
  }
  return z.object(shape) as unknown as z.ZodObject<IndexSearchShape<F, S>>;
}

/** Query params for a list request: every value a string, except `limit`. */
export type ListParams = Record<string, string | number>;

/**
 * The API params for a route's validated search: `q`, `sort`, the flat filter
 * params (unknown enum values dropped), `cursor` and `limit` when set. The
 * loader and `useSearchListState().params` both come from here, so the
 * prefetched query and the page's query share one key.
 */
export function listParams(
  search: Readonly<Record<string, unknown>>,
  filters: readonly FilterField[] = [],
): ListParams {
  const out: ListParams = {};
  const q = nonEmpty(typeof search.q === 'string' ? search.q : undefined);
  const sort = nonEmpty(typeof search.sort === 'string' ? search.sort : undefined);
  if (q) out.q = q;
  if (sort) out.sort = sort;
  const values = parseFilterValues(filters, search);
  for (const [name, value] of Object.entries(serializeFilterValues(filters, values))) {
    if (value !== null) out[name] = value;
  }
  if (typeof search.cursor === 'string' && search.cursor !== '') out.cursor = search.cursor;
  if (typeof search.limit === 'number') out.limit = search.limit;
  return out;
}
